import React, { createContext, useContext, useState, useEffect } from 'react';
import fileSystemService from '../../../services/FileSystemService';
import firebaseService from '../services/FirebaseService';
import supabaseService from '../../../services/SupabaseService';
import { useAuth } from '../../../contexts/AuthContext';
import UserTierService from '../../../models/UserTier';

const DraftContext = createContext();

export const MAP_OPTIONS = ['Broken Wall', 'Dangerous Grass', 'Expanding River', 'Flying Cloud', 'General'];

const createDefaultScenarios = (pickMode) => [
  { id: 0, title: 'Scenario 1', picks: Array(24).fill(null), swapSides: pickMode === 'second' }
];

const createInitialMapDrafts = () => {
  const initial = {};
  MAP_OPTIONS.forEach(map => {
    initial[map] = {
      scenariosFirstPick: createDefaultScenarios('first'),
      scenariosSecondPick: createDefaultScenarios('second')
    };
  });
  return initial;
};

export function DraftProvider({ children }) {
  // Get user tier info from AuthContext
  const { userProfile, currentUser } = useAuth();

  // Info fields
  const [teamName, setTeamName] = useState('');
  const [tournament, setTournament] = useState('');
  const [stage, setStage] = useState('');
  const [draftDate, setDraftDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  // Pick mode state - shared across components
  const [pickMode, setPickMode] = useState('first');

  // Map selection state
  const [selectedMap, setSelectedMap] = useState('General');

  // Board layout state (which columns are visible and their labels) — kept here so undo/redo covers them
  const [boardMaps, setBoardMaps] = useState(['General']);
  const [boardLabels, setBoardLabels] = useState(['General']);

  // All maps' draft data stored in a single state object
  const [mapDrafts, setMapDrafts] = useState(() => createInitialMapDrafts());

  // 15 forehead hero slots — saved with the draft
  const [foreheadPicks, setForeheadPicks] = useState(() => Array(15).fill(null));
  // Connector arrows between the 15 forehead slots — saved with the draft
  const [foreheadConnectors, setForeheadConnectors] = useState([]);

  // Derived from selected map (for backward compat with all existing functions)
  const scenariosFirstPick = mapDrafts[selectedMap]?.scenariosFirstPick || createDefaultScenarios('first');
  const scenariosSecondPick = mapDrafts[selectedMap]?.scenariosSecondPick || createDefaultScenarios('second');

  // Local setters that write into mapDrafts[selectedMap] - all existing functions use these
  const setScenariosFirstPick = (updater) => {
    setMapDrafts(prev => {
      const current = prev[selectedMap]?.scenariosFirstPick || createDefaultScenarios('first');
      const updated = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [selectedMap]: { ...prev[selectedMap], scenariosFirstPick: updated } };
    });
  };

  const setScenariosSecondPick = (updater) => {
    setMapDrafts(prev => {
      const current = prev[selectedMap]?.scenariosSecondPick || createDefaultScenarios('second');
      const updated = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [selectedMap]: { ...prev[selectedMap], scenariosSecondPick: updated } };
    });
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [downloadedDrafts, setDownloadedDrafts] = useState([]);
  const [isFileSystemApiSupported, setIsFileSystemApiSupported] = useState(false);
  const [selectedFolderPath, setSelectedFolderPath] = useState(null);
  const [originalDraftData, setOriginalDraftData] = useState(null);

  // Undo/Redo state management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);

  // Track pulled match heroes for highlighting (array of match data)
  const [pulledMatchHeroes, setPulledMatchHeroes] = useState([]);

  // Check if File System Access API is supported
  useEffect(() => {
    setIsFileSystemApiSupported(!!window.showDirectoryPicker);
    const savedPath = fileSystemService.getSavedFolderPath();
    if (savedPath) {
      setSelectedFolderPath(savedPath);
    }
    preCacheExistingDrafts();
  }, []);

  // Pre-cache existing drafts from tracking
  const preCacheExistingDrafts = () => {
    try {
      const tracking = fileSystemService.getDraftTracking();
      if (!tracking) return;
      Object.keys(tracking).forEach(draftId => {
        const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
        if (!cachedDraftJson) {
          console.log(`Draft ${draftId} not cached yet`);
        }
      });
    } catch (error) {
      console.error('Error pre-caching drafts:', error);
    }
  };

  // Block browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Save current state to history (for undo/redo)
  useEffect(() => {
    if (isUndoRedoAction) {
      setIsUndoRedoAction(false);
      return;
    }
    const currentState = {
      mapDrafts: JSON.parse(JSON.stringify(mapDrafts)),
      foreheadPicks: JSON.parse(JSON.stringify(foreheadPicks)),
      foreheadConnectors: JSON.parse(JSON.stringify(foreheadConnectors)),
      teamName,
      tournament,
      stage,
      draftDate,
      boardMaps: [...boardMaps],
      boardLabels: [...boardLabels],
    };
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDrafts, foreheadPicks, foreheadConnectors, teamName, tournament, stage, draftDate, boardMaps, boardLabels]);

  // Undo function
  const undo = () => {
    if (historyIndex <= 0) return;
    const previousState = history[historyIndex - 1];
    if (!previousState) return;
    setIsUndoRedoAction(true);
    setMapDrafts(JSON.parse(JSON.stringify(previousState.mapDrafts)));
    if (previousState.foreheadPicks) setForeheadPicks(JSON.parse(JSON.stringify(previousState.foreheadPicks)));
    if (previousState.foreheadConnectors) setForeheadConnectors(JSON.parse(JSON.stringify(previousState.foreheadConnectors)));
    setTeamName(previousState.teamName);
    setTournament(previousState.tournament);
    setStage(previousState.stage);
    setDraftDate(previousState.draftDate);
    if (previousState.boardMaps) setBoardMaps([...previousState.boardMaps]);
    if (previousState.boardLabels) setBoardLabels([...previousState.boardLabels]);
    setHistoryIndex(prev => prev - 1);
    setHasUnsavedChanges(true);
  };

  // Redo function
  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const nextState = history[historyIndex + 1];
    if (!nextState) return;
    setIsUndoRedoAction(true);
    setMapDrafts(JSON.parse(JSON.stringify(nextState.mapDrafts)));
    if (nextState.foreheadPicks) setForeheadPicks(JSON.parse(JSON.stringify(nextState.foreheadPicks)));
    if (nextState.foreheadConnectors) setForeheadConnectors(JSON.parse(JSON.stringify(nextState.foreheadConnectors)));
    setTeamName(nextState.teamName);
    setTournament(nextState.tournament);
    setStage(nextState.stage);
    setDraftDate(nextState.draftDate);
    if (nextState.boardMaps) setBoardMaps([...nextState.boardMaps]);
    if (nextState.boardLabels) setBoardLabels([...nextState.boardLabels]);
    setHistoryIndex(prev => prev + 1);
    setHasUnsavedChanges(true);
  };

  // Get all maps' data directly from mapDrafts state
  const getAllMapDrafts = () => {
    return JSON.parse(JSON.stringify(mapDrafts));
  };

  // Add scenario for a pick mode (with tier checks). mapName defaults to selectedMap.
  const addScenario = (pickMode, mapName = null) => {
    const targetMap = mapName || selectedMap;
    setMapDrafts(prev => {
      const mapData = prev[targetMap] || {
        scenariosFirstPick: createDefaultScenarios('first'),
        scenariosSecondPick: createDefaultScenarios('second')
      };
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      const scenarios = mapData[key] || [];

      // Check tier limits before adding
      if (UserTierService && UserTierService.canAddScenario) {
        const limitCheck = UserTierService.canAddScenario(userProfile, scenarios.length, 0);
        if (!limitCheck.canAdd) {
          alert(limitCheck.reason);
          return prev; // no change
        }
      }

      const maxId = scenarios.length > 0 ? Math.max(...scenarios.map(s => s.id)) : -1;
      const newScenario = {
        id: maxId + 1,
        title: `Scenario ${maxId + 2}`,
        picks: Array(24).fill(null),
        swapSides: pickMode === 'second'
      };
      return {
        ...prev,
        [targetMap]: { ...mapData, [key]: [...scenarios, newScenario] }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Duplicate scenario for a pick mode (with tier checks, inserts after source). mapName defaults to selectedMap.
  const duplicateScenario = (pickMode, scenarioId, mapName = null) => {
    const targetMap = mapName || selectedMap;
    setMapDrafts(prev => {
      const mapData = prev[targetMap] || {
        scenariosFirstPick: createDefaultScenarios('first'),
        scenariosSecondPick: createDefaultScenarios('second')
      };
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      const scenarios = mapData[key] || [];

      if (UserTierService && UserTierService.canAddScenario) {
        const canAddResult = UserTierService.canAddScenario(userProfile, scenarios.length);
        if (!canAddResult.canAdd) {
          alert(canAddResult.reason);
          return prev;
        }
      }

      const scenarioToDuplicate = scenarios.find(s => s.id === scenarioId);
      if (!scenarioToDuplicate) return prev;

      const scenarioIndex = scenarios.findIndex(s => s.id === scenarioId);
      const maxId = Math.max(...scenarios.map(s => s.id));
      const newScenario = {
        ...scenarioToDuplicate,
        id: maxId + 1,
        title: `${scenarioToDuplicate.title} (Copy)`,
        picks: JSON.parse(JSON.stringify(scenarioToDuplicate.picks)),
        practiceMatchups: scenarioToDuplicate.practiceMatchups
          ? JSON.parse(JSON.stringify(scenarioToDuplicate.practiceMatchups))
          : {}
      };

      const newScenarios = [
        ...scenarios.slice(0, scenarioIndex + 1),
        newScenario,
        ...scenarios.slice(scenarioIndex + 1)
      ];
      return { ...prev, [targetMap]: { ...mapData, [key]: newScenarios } };
    });
    setHasUnsavedChanges(true);
  };

  // Delete scenario for a pick mode. mapName defaults to selectedMap.
  const deleteScenario = (pickMode, scenarioId, mapName = null) => {
    const targetMap = mapName || selectedMap;
    setMapDrafts(prev => {
      const mapData = prev[targetMap];
      if (!mapData) return prev;
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      const scenarios = mapData[key] || [];
      if (scenarios.length <= 1) return prev;
      return {
        ...prev,
        [targetMap]: { ...mapData, [key]: scenarios.filter(s => s.id !== scenarioId) }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Reset all non-General maps to default empty scenarios (used by "Add Maps")
  const resetNonGeneralMapDrafts = () => {
    setMapDrafts(prev => {
      const next = { ...prev };
      MAP_OPTIONS.forEach(map => {
        if (map !== 'General') {
          next[map] = {
            scenariosFirstPick: createDefaultScenarios('first'),
            scenariosSecondPick: createDefaultScenarios('second'),
          };
        }
      });
      return next;
    });
    setHasUnsavedChanges(true);
  };

  // Update scenario for a pick mode. mapName defaults to selectedMap.
  const updateScenario = (pickMode, scenarioId, updates, mapName = null) => {
    const targetMap = mapName || selectedMap;
    setMapDrafts(prev => {
      const mapData = prev[targetMap];
      if (!mapData) return prev;
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      return {
        ...prev,
        [targetMap]: {
          ...mapData,
          [key]: (mapData[key] || []).map(s => s.id === scenarioId ? { ...s, ...updates } : s)
        }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Initialize a new map key with defaults if it doesn't already exist.
  const initializeMap = (mapKey) => {
    setMapDrafts(prev => {
      if (prev[mapKey]) return prev;
      return {
        ...prev,
        [mapKey]: {
          scenariosFirstPick: createDefaultScenarios('first'),
          scenariosSecondPick: createDefaultScenarios('second')
        }
      };
    });
  };

  // Move scenario from one map to another, inserting at toIndex. Atomic update.
  const moveScenarioToMap = (pickMode, scenarioId, fromMap, toMap, toIndex = null) => {
    setMapDrafts(prev => {
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      const fromMapData = prev[fromMap];
      const toMapData = prev[toMap] || {
        scenariosFirstPick: createDefaultScenarios('first'),
        scenariosSecondPick: createDefaultScenarios('second')
      };
      if (!fromMapData) return prev;

      const fromScenarios = [...(fromMapData[key] || [])];
      const scenarioIndex = fromScenarios.findIndex(s => s.id === scenarioId);
      if (scenarioIndex === -1) return prev;

      const [movedScenario] = fromScenarios.splice(scenarioIndex, 1);

      const toScenarios = [...(toMapData[key] || [])];
      const maxId = toScenarios.length > 0 ? Math.max(...toScenarios.map(s => s.id)) : -1;
      const scenarioToInsert = { ...movedScenario, id: maxId + 1 };

      const insertAt = toIndex !== null && toIndex >= 0 && toIndex <= toScenarios.length
        ? toIndex
        : toScenarios.length;
      toScenarios.splice(insertAt, 0, scenarioToInsert);

      return {
        ...prev,
        [fromMap]: { ...fromMapData, [key]: fromScenarios },
        [toMap]: { ...toMapData, [key]: toScenarios }
      };
    });
    setHasUnsavedChanges(true);
  };

  // Reorder scenarios for a pick mode. mapName defaults to selectedMap.
  const reorderScenarios = (pickMode, fromIndex, toIndex, mapName = null) => {
    const targetMap = mapName || selectedMap;
    setMapDrafts(prev => {
      const mapData = prev[targetMap];
      if (!mapData) return prev;
      const key = pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick';
      const scenarios = [...(mapData[key] || [])];
      const [movedScenario] = scenarios.splice(fromIndex, 1);
      scenarios.splice(toIndex, 0, movedScenario);
      return { ...prev, [targetMap]: { ...mapData, [key]: scenarios } };
    });
    setHasUnsavedChanges(true);
  };

  // Detect if current draft has significant changes compared to original
  const hasSignificantChanges = () => {
    if (!originalDraftData || !currentDraftId) return false;
    if (originalDraftData.teamName !== teamName ||
        originalDraftData.tournament !== tournament ||
        originalDraftData.stage !== stage ||
        originalDraftData.date !== draftDate) {
      return true;
    }
    // Compare all map data
    const originalAllMaps = originalDraftData.mapDrafts;
    if (originalAllMaps) {
      if (JSON.stringify(mapDrafts) !== JSON.stringify(originalAllMaps)) {
        return true;
      }
    } else {
      // Legacy: compare top-level scenarios only (General map)
      const genFirst = mapDrafts['General']?.scenariosFirstPick || [];
      const genSecond = mapDrafts['General']?.scenariosSecondPick || [];
      if (JSON.stringify(genFirst) !== JSON.stringify(originalDraftData.scenariosFirstPick || []) ||
          JSON.stringify(genSecond) !== JSON.stringify(originalDraftData.scenariosSecondPick || [])) {
        return true;
      }
    }
    return false;
  };

  // Save draft (local filesystem + Firebase cloud)
  const saveDraft = async (draftInfo = {}) => {
    setLoading(true);
    setError(null);
    try {
      const shouldCreateNew = currentDraftId && hasSignificantChanges();

      const draftData = {
        id: shouldCreateNew ? null : currentDraftId,
        selectedMap,
        boardMaps: [...boardMaps],
        boardLabels: [...boardLabels],
        mapDrafts: getAllMapDrafts(),
        foreheadPicks,
        foreheadConnectors,
        teamName,
        tournament,
        stage,
        title: draftInfo.title || `${teamName || 'Team'}_${tournament || 'Tournament'}_${stage || 'Stage'}_${draftDate}`,
        date: draftDate,
        ...draftInfo
      };

      const savedId = await fileSystemService.saveDraft(draftData);

      // Online saving disabled — local filesystem only for mlbbdraftplan.
      // To re-enable Firebase + Supabase saves, restore the if (currentUser) block here.

      setCurrentDraftId(savedId);
      setHasUnsavedChanges(false);

      if (shouldCreateNew) {
        setOriginalDraftData({
          mapDrafts: getAllMapDrafts(),
          selectedMap,
          teamName,
          tournament,
          stage,
          date: draftDate
        });
      }

      setLoading(false);
      return savedId;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Load draft (both pick modes and info fields)
  const loadDraft = async (draftId, filename, skipConfirmation = false) => {
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true, draftId, filename };
    }

    setLoading(true);
    setError(null);
    try {
      console.log(`Loading draft: ${draftId}, filename: ${filename}`);

      const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
      if (cachedDraftJson) {
        try {
          const cachedDraft = JSON.parse(cachedDraftJson);
          loadDraftData(cachedDraft);
          setLoading(false);
          return cachedDraft;
        } catch (cacheError) {
          console.warn('Could not load cached draft, falling back to file', cacheError);
        }
      }

      const draftData = await fileSystemService.getDraft(draftId, filename);
      if (!draftData) throw new Error('Draft not found');

      loadDraftData(draftData);

      try {
        localStorage.setItem(`draft_cache_${draftId}`, JSON.stringify(draftData));
      } catch (cacheError) {
        console.warn('Could not cache draft data', cacheError);
      }

      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Helper function to load draft data into state
  const loadDraftData = (draftData) => {
    // Restore map selection
    const loadedMap = draftData.selectedMap || 'General';
    setSelectedMap(loadedMap);

    // Restore mapDrafts from saved data
    if (draftData.mapDrafts) {
      // Start with defaults for all standard maps
      const loadedMapDrafts = { ...createInitialMapDrafts() };
      // Restore all saved map data (both standard and custom slot keys)
      Object.keys(draftData.mapDrafts).forEach(mapName => {
        const saved = draftData.mapDrafts[mapName];
        loadedMapDrafts[mapName] = {
          scenariosFirstPick: saved.scenariosFirstPick?.length > 0
            ? saved.scenariosFirstPick
            : createDefaultScenarios('first'),
          scenariosSecondPick: saved.scenariosSecondPick?.length > 0
            ? saved.scenariosSecondPick
            : createDefaultScenarios('second'),
        };
      });
      setMapDrafts(loadedMapDrafts);
    } else {
      // Backward compat: old save format with top-level scenariosFirstPick/scenariosSecondPick
      // Put them into the 'General' map slot
      const loadedMapDrafts = createInitialMapDrafts();
      const firstPick = draftData.scenariosFirstPick
        || [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }];
      const secondPick = draftData.scenariosSecondPick
        || [{ id: 0, title: 'Scenario 1', picks: Array(24).fill(null) }];
      loadedMapDrafts['General'] = { scenariosFirstPick: firstPick, scenariosSecondPick: secondPick };
      setMapDrafts(loadedMapDrafts);
    }

    setTeamName(draftData.teamName || '');
    setTournament(draftData.tournament || '');
    setStage(draftData.stage || '');
    setDraftDate(draftData.date || (new Date().toISOString().slice(0, 10)));
    setCurrentDraftId(draftData.id);
    setHasUnsavedChanges(false);
    setBoardMaps(draftData.boardMaps ? [...draftData.boardMaps] : ['General']);
    setBoardLabels(draftData.boardLabels ? [...draftData.boardLabels] : ['General']);

    const fh = Array.isArray(draftData.foreheadPicks) && draftData.foreheadPicks.length === 15
      ? draftData.foreheadPicks
      : Array(15).fill(null);
    const fc = Array.isArray(draftData.foreheadConnectors) ? draftData.foreheadConnectors : [];
    setForeheadPicks(fh);
    setForeheadConnectors(fc);

    // Rebuild pulledMatchHeroes from all maps' scenarios
    const rebuiltMatchHeroes = [];
    const allMapData = draftData.mapDrafts || { 'General': {
      scenariosFirstPick: draftData.scenariosFirstPick || [],
      scenariosSecondPick: draftData.scenariosSecondPick || []
    }};
    Object.values(allMapData).forEach(mapData => {
      const allScenarios = [
        ...(mapData.scenariosFirstPick || []),
        ...(mapData.scenariosSecondPick || [])
      ];
      allScenarios.forEach(scenario => {
        if (scenario.fromMatchPull && scenario.picks) {
          const matchId = scenario.matchId || 'unknown';
          scenario.picks.forEach((pick, index) => {
            if (pick && pick.hero && pick.hero.id) {
              rebuiltMatchHeroes.push({ heroId: pick.hero.id, position: index + 1, matchId });
            }
          });
        }
      });
    });
    setPulledMatchHeroes(rebuiltMatchHeroes);

    // Reset undo/redo history
    setHistory([]);
    setHistoryIndex(-1);

    setOriginalDraftData({
      mapDrafts: draftData.mapDrafts ? JSON.parse(JSON.stringify(draftData.mapDrafts)) : null,
      selectedMap: loadedMap,
      teamName: draftData.teamName || '',
      tournament: draftData.tournament || '',
      stage: draftData.stage || '',
      date: draftData.date || ''
    });
  };

  // Import draft data directly (from session storage, drag-and-drop, etc.)
  const importDraftData = async (draftData, skipConfirmation = false) => {
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true, draftData };
    }
    setLoading(true);
    setError(null);
    try {
      if (!draftData) throw new Error('No draft data provided');
      loadDraftData(draftData);
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Merge imported draft data with current draft
  const mergeDraftData = (importedData) => {
    const importedFirstPick = importedData.scenariosFirstPick || [];
    const importedSecondPick = importedData.scenariosSecondPick || [];

    const maxFirstPickId = scenariosFirstPick.length > 0
      ? Math.max(...scenariosFirstPick.map(s => s.id))
      : -1;
    const maxSecondPickId = scenariosSecondPick.length > 0
      ? Math.max(...scenariosSecondPick.map(s => s.id))
      : -1;

    const renumberedFirstPick = importedFirstPick.map((scenario, index) => ({
      ...scenario,
      id: maxFirstPickId + 1 + index,
      title: scenario.title || `Imported Scenario ${maxFirstPickId + 2 + index}`
    }));

    const renumberedSecondPick = importedSecondPick.map((scenario, index) => ({
      ...scenario,
      id: maxSecondPickId + 1 + index,
      title: scenario.title || `Imported Scenario ${maxSecondPickId + 2 + index}`
    }));

    setScenariosFirstPick(prev => [...prev, ...renumberedFirstPick]);
    setScenariosSecondPick(prev => [...prev, ...renumberedSecondPick]);
    setHasUnsavedChanges(true);
  };

  // Load draft directly from file handle
  const loadDraftFromHandle = async (fileHandle) => {
    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFromHandle(fileHandle);
      if (!draftData) throw new Error('Draft not found');
      loadDraftData(draftData);
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Scan for drafts in the selected folder
  const scanDownloadsForDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDownloadsForDrafts();
      setDownloadedDrafts(drafts);
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) setSelectedFolderPath(savedPath);
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Try to scan the default Downloads folder
  const scanDefaultDownloadsFolder = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = await fileSystemService.scanDefaultDownloadsFolder();
      setDownloadedDrafts(drafts);
      const savedPath = fileSystemService.getSavedFolderPath();
      if (savedPath) setSelectedFolderPath(savedPath);
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Import a draft from file
  const importDraft = async (skipConfirmation = false) => {
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }
    setLoading(true);
    setError(null);
    try {
      const draftData = await fileSystemService.loadDraftFile();
      if (!draftData) throw new Error('Failed to import draft');
      loadDraftData(draftData);
      setLoading(false);
      return draftData;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Get all drafts
  const getDrafts = async () => {
    setLoading(true);
    setError(null);
    try {
      const drafts = fileSystemService.getDrafts();
      setLoading(false);
      return drafts;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Delete a draft
  const deleteDraft = async (draftId) => {
    setLoading(true);
    setError(null);
    try {
      const success = fileSystemService.deleteDraft(draftId);
      setLoading(false);
      if (currentDraftId === draftId) {
        setMapDrafts(createInitialMapDrafts());
        setSelectedMap('General');
        setTeamName('');
        setTournament('');
        setStage('');
        setDraftDate(new Date().toISOString().slice(0, 10));
        setCurrentDraftId(null);
        setHasUnsavedChanges(false);
      }
      return success;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Create a new draft (reset to initial state)
  const newDraft = (skipConfirmation = false) => {
    if (!skipConfirmation && (hasUnsavedChanges || hasSignificantChanges())) {
      return { needsConfirmation: true };
    }
    setMapDrafts(createInitialMapDrafts());
    setSelectedMap('General');
    setBoardMaps(['General']);
    setBoardLabels(['General']);
    setForeheadPicks(Array(15).fill(null));
    setForeheadConnectors([]);
    setTeamName('');
    setTournament('');
    setStage('');
    setDraftDate(new Date().toISOString().slice(0, 10));
    setCurrentDraftId(null);
    setHasUnsavedChanges(false);
    setOriginalDraftData(null);
    setPulledMatchHeroes([]);
    setHistory([]);
    setHistoryIndex(-1);
    return { success: true };
  };

  const value = {
    scenariosFirstPick,
    scenariosSecondPick,
    mapDrafts,
    pickMode,
    setPickMode,
    addScenario,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    resetNonGeneralMapDrafts,
    reorderScenarios,
    moveScenarioToMap,
    initializeMap,
    saveDraft,
    loadDraft,
    loadDraftFromHandle,
    importDraft,
    importDraftData,
    mergeDraftData,
    getDrafts,
    scanDownloadsForDrafts,
    scanDefaultDownloadsFolder,
    downloadedDrafts,
    isFileSystemApiSupported,
    newDraft,
    deleteDraft,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    draftDate, setDraftDate,
    hasUnsavedChanges, setHasUnsavedChanges,
    hasSignificantChanges,
    currentDraftId,
    loading,
    error,
    selectedFolderPath,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    selectedMap,
    setSelectedMap,
    boardMaps,
    setBoardMaps,
    boardLabels,
    setBoardLabels,
    pulledMatchHeroes,
    setPulledMatchHeroes,
    foreheadPicks,
    setForeheadPicks,
    foreheadConnectors,
    setForeheadConnectors,
    userProfile
  };

  return (
    <DraftContext.Provider value={value}>
      {children}
    </DraftContext.Provider>
  );
}

export function useDraft() {
  return useContext(DraftContext);
}
